import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface TrajectstapPubliek {
  id: string;
  status: string; // pending | active | completed
  volgorde: number;
  geplandeDatum: string | null;
  afgerondDatum: string | null;
  template: {
    naam: string; // JSON: {nl, fr, en}
    icon: string;
    kleur: string;
  };
}

interface ApparaatPubliek {
  id: string;
  naam: string;
  merk: string | null;
  status: string;
  binnenDatum: string | null;
}

interface DocumentPubliek {
  id: string;
  naam: string;
  documentType: string | null;
  mimeType: string;
  createdAt: string;
}

export interface BerichtPubliek {
  id: string;
  content: string;
  senderType: string; // customer | agent | system | field_worker
  channel: string;
  createdAt: string;
  readAt: string | null;
  milestoneId: string | null;
  sender?: { firstName: string; lastName: string } | null;
  attachments: { id: string; filename: string; mimeType: string; fileSize?: number }[];
}

export interface AfspraakDatum {
  id: string;
  datum: string;
  vanUur: string | null;
  totUur: string | null;
}

export interface AfspraakPubliek {
  id: string;
  titel: string;
  beschrijving: string | null;
  status: string; // voorstel | bevestigd | geweigerd | verlopen
  bevestigdeDatum: string | null;
  createdAt: string;
  datums: AfspraakDatum[];
  trajectstap: {
    template: { naam: string; icon: string };
  };
  aanmaker: { firstName: string; lastName: string };
}

export interface DossierPubliek {
  id: string;
  dossierNummer: string;
  klantNaam: string;
  adres: string;
  postcode: string;
  stad: string;
  status: string;
  leveringsdatum: string | null;
  plaatsingsdatumStart: string | null;
  plaatsingsdatumEind: string | null;
  createdAt: string;
  updatedAt: string;
  entity: { name: string; phone: string | null; email: string | null };
  trajectstappen: TrajectstapPubliek[];
  apparaten: ApparaatPubliek[];
  documenten: DocumentPubliek[];
}

async function fetchPubliekDossier(token: string): Promise<DossierPubliek> {
  const res = await fetch(`/api/klantportaal/${token}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Dossier niet gevonden');
  }
  return res.json();
}

async function fetchBerichten(token: string, fase?: string): Promise<BerichtPubliek[]> {
  const params = fase ? `?fase=${fase}` : '';
  const res = await fetch(`/api/klantportaal/${token}/berichten${params}`);
  if (!res.ok) throw new Error('Berichten ophalen mislukt');
  return res.json();
}

async function sendBericht(token: string, content: string, naam?: string, fase?: string, fotos?: File[]): Promise<BerichtPubliek> {
  if (fotos && fotos.length > 0) {
    const formData = new FormData();
    formData.append('content', content);
    if (naam) formData.append('naam', naam);
    if (fase) formData.append('fase', fase);
    for (const foto of fotos) formData.append('fotos', foto);
    const res = await fetch(`/api/klantportaal/${token}/berichten`, {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'Bericht versturen mislukt');
    }
    return res.json();
  }
  const res = await fetch(`/api/klantportaal/${token}/berichten`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, naam, fase }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Bericht versturen mislukt');
  }
  return res.json();
}

async function fetchAfspraken(token: string): Promise<AfspraakPubliek[]> {
  const res = await fetch(`/api/klantportaal/${token}/afspraken`);
  if (!res.ok) throw new Error('Afspraken ophalen mislukt');
  return res.json();
}

export function usePubliekDossier(token: string) {
  return useQuery({
    queryKey: ['klantportaal', token],
    queryFn: () => fetchPubliekDossier(token),
    enabled: !!token,
    retry: false,
    refetchInterval: 30000,
  });
}

export function usePubliekBerichten(token: string, fase?: string) {
  return useQuery({
    queryKey: ['klantportaal', token, 'berichten', fase],
    queryFn: () => fetchBerichten(token, fase),
    enabled: !!token,
    refetchInterval: 15000,
  });
}

export function useSendPubliekBericht(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ content, naam, fase, fotos }: { content: string; naam?: string; fase?: string; fotos?: File[] }) =>
      sendBericht(token, content, naam, fase, fotos),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['klantportaal', token, 'berichten'] });
    },
  });
}

export function useMarkeerGelezen(token: string) {
  return useMutation({
    mutationFn: async (berichtId: string) => {
      const res = await fetch(`/api/klantportaal/${token}/berichten/${berichtId}/gelezen`, {
        method: 'PUT',
      });
      if (!res.ok) throw new Error('Markeren als gelezen mislukt');
      return res.json();
    },
  });
}

export function usePubliekAfspraken(token: string) {
  return useQuery({
    queryKey: ['klantportaal', token, 'afspraken'],
    queryFn: () => fetchAfspraken(token),
    enabled: !!token,
  });
}

export function useBevestigAfspraak(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ afspraakId, datumId }: { afspraakId: string; datumId: string }) => {
      const res = await fetch(`/api/klantportaal/${token}/afspraken/${afspraakId}/bevestigen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ datumId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Bevestigen mislukt');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['klantportaal', token, 'afspraken'] });
    },
  });
}

export interface BrandingPubliek {
  logoUrl: string | null;
  bedrijfsNaam: string | null;
  kleurPrimair: string | null;
  kleurSecundair: string | null;
  telefoon: string | null;
  email: string | null;
  website: string | null;
}

export function usePortaalBranding(token: string) {
  return useQuery<BrandingPubliek | null>({
    queryKey: ['klantportaal', token, 'branding'],
    queryFn: async () => {
      const res = await fetch(`/api/klantportaal/${token}/branding`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!token,
    staleTime: 300000,
  });
}

export function useSubmitFeedback(token: string) {
  return useMutation({
    mutationFn: async ({ score, comment }: { score: number; comment?: string }) => {
      const res = await fetch(`/api/klantportaal/${token}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score, comment }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { message?: string }).message || 'Feedback versturen mislukt');
      }
      return res.json();
    },
  });
}

export function useSubmitSchade(token: string) {
  return useMutation({
    mutationFn: async ({ beschrijving, naam, fotos }: { beschrijving: string; naam?: string; fotos?: File[] }) => {
      const formData = new FormData();
      formData.append('beschrijving', beschrijving);
      if (naam) formData.append('naam', naam);
      if (fotos) {
        for (const foto of fotos) formData.append('fotos', foto);
      }
      const res = await fetch(`/api/klantportaal/${token}/schade`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { message?: string }).message || 'Schade melden mislukt');
      }
      return res.json();
    },
  });
}

export function useWeigerAfspraak(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (afspraakId: string) => {
      const res = await fetch(`/api/klantportaal/${token}/afspraken/${afspraakId}/weigeren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Weigeren mislukt');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['klantportaal', token, 'afspraken'] });
    },
  });
}

export interface ActiviteitPubliek {
  id: string;
  type: string;
  tijdstip: string;
  tekst: string;
  icon?: string;
}

export function usePubliekActiviteiten(token: string) {
  return useQuery<ActiviteitPubliek[]>({
    queryKey: ['klantportaal', token, 'activiteiten'],
    queryFn: async () => {
      const res = await fetch(`/api/klantportaal/${token}/activiteiten`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!token,
    refetchInterval: 30000,
  });
}
